import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { getExams } from "../../services/Exams/ExamsService";
import { getEntityLabel } from "../../utils/entityLabel";
import { useRowLink } from "../../utils/rowLink";
import { fetchAllPages } from "../../utils/fetchAllPages";
import { formatDate } from "../../utils/formatDate";
import { filterSelectStyles, selectTheme } from "../../components/forms/Select/styles";
import FilterChips from "../../components/QuestionList/FilterChips";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import { Dash } from "../../components/QuestionList/cells";
import { focusRing, useMediaQuery } from "./TestBuilder/ui";
import { EXAM_STATUS_TONES, ExamStatusBadge, ExamStatusDialog, examStatus, examStatusLabel } from "./ExamStatus";
import { HelpTitle } from "../../components/Help/HelpButton";
import ExamsGuide from "../../components/Help/guides/ExamsGuide";

// The whole list is loaded (the filters and the sort work on it here), then shown by pages of this size;
// search results come all on one page, to scroll through.
const PAGE_SIZE = 30;
const DEFAULT_ORDERING = "-created";

// The one primary action: 44px on phones (the cards' layout), 40px beside the table.
const createButtonClass = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 md:h-10 ${focusRing}`;
const clearButtonClass = `mt-4 inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`;
const headerCellClass = "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500";
const panelClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";

const examHref = (exam) => `/exams_list/${exam.uuid}`;

// The exam's category as {value, label}, or null: the API gives its uuid and `category_name`.
const examCategory = (exam) => {
  const category = exam?.category;
  if (category === null || category === undefined || category === "") return null;
  const value = typeof category === "object" ? category.uuid || getEntityLabel(category, "") : String(category);
  if (!value) return null;
  return { value, label: exam.category_name || (typeof category === "object" ? getEntityLabel(category, "") : "") };
};

// The description under the name, unless it only repeats the name.
const examDescription = (exam) => {
  const description = (exam?.description || "").trim();
  return description && description.toLowerCase() !== (exam?.name || "").trim().toLowerCase() ? description : "";
};

const createdTime = (exam) => {
  const time = Date.parse(exam?.created);
  return Number.isNaN(time) ? 0 : time;
};

// By name in the reader's language (numbers in order: "Day 2" before "Day 10"); unnamed exams last.
const byName = (collator, direction) => (a, b) => {
  const left = (a?.name || "").trim();
  const right = (b?.name || "").trim();
  if (!left || !right) return (left ? 0 : 1) - (right ? 0 : 1);
  return direction * collator.compare(left, right);
};

const sorter = (ordering, collator) => {
  switch (ordering) {
    case "created":
      return (a, b) => createdTime(a) - createdTime(b);
    case "name":
      return byName(collator, 1);
    case "-name":
      return byName(collator, -1);
    default:
      return (a, b) => createdTime(b) - createdTime(a);
  }
};

// The exam's status, a button that opens the status change (the row around it opens the exam). Its name says
// which exam it changes: the list has many alike.
function StatusButton({ exam, onChange }) {
  const [t] = useTranslation();
  const status = examStatus(exam);
  if (!status) return <Dash />;
  const label = t("exam_bank.status_actions.change_label", {
    name: exam.name?.trim() || t("exam_bank.untitled"),
    status: examStatusLabel(t, status),
  });
  return (
    <button
      type="button"
      onClick={() => onChange(exam)}
      aria-haspopup="dialog"
      aria-label={label}
      title={t("exam_bank.status_actions.change")}
      className={`group -mx-1 inline-flex items-center gap-1 rounded-full px-1 py-0.5 transition-colors duration-200 hover:bg-slate-100 max-md:min-h-[44px] ${focusRing}`}
    >
      <ExamStatusBadge status={status} />
      <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-400 transition-colors duration-200 group-hover:text-slate-700" aria-hidden="true" />
    </button>
  );
}

// A column header that sorts the list (`field` ascending, then descending), as the sort menu does.
function SortHeader({ field, ordering, onSort, className = "", children }) {
  const sort = ordering === field ? "ascending" : ordering === `-${field}` ? "descending" : undefined;
  const Icon = sort === "descending" ? ChevronDownIcon : ChevronUpIcon;
  return (
    <th scope="col" aria-sort={sort} className={`${headerCellClass} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`group -mx-1 inline-flex items-center gap-1.5 whitespace-nowrap rounded px-1 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 hover:text-slate-800 ${focusRing}`}
      >
        {children}
        <Icon
          className={`h-4 w-4 shrink-0 ${
            sort ? "text-blue-600" : "text-slate-400 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
          }`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

function EmptyState({ Icon, title, hint, onClear }) {
  const [t] = useTranslation();
  return (
    <div className={`flex flex-col items-center justify-center px-6 py-16 text-center ${panelClass}`}>
      <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <h3 className="text-sm font-medium text-slate-900">{title}</h3>
      {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
      {onClear && (
        <button type="button" onClick={onClear} className={clearButtonClass}>
          {t("question_list.clear_all")}
        </button>
      )}
    </div>
  );
}

export default function ExamsList() {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const rowLink = useRowLink();
  // Phones get the list as cards instead of the table.
  const wide = useMediaQuery("(min-width: 768px)");
  const resultsRef = useRef(null);

  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [ordering, setOrdering] = useState(DEFAULT_ORDERING);
  const [page, setPage] = useState(1);
  // The status dialog and its exam (kept while the dialog closes).
  const [statusExam, setStatusExam] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const openStatus = (exam) => {
    setStatusExam(exam);
    setStatusOpen(true);
  };

  useEffect(() => {
    const controller = new AbortController();
    // A request left behind (the page closed, or React's development double mount) changes nothing.
    let active = true;
    const fetchExams = async () => {
      try {
        // Every page of the list: the server pages by 30, so a bare request missed the older exams.
        const rows = await fetchAllPages((pageParams) => getExams({ params: pageParams, signal: controller.signal }));
        if (!active) return;
        setExams(Array.isArray(rows) ? rows : []);
        setError(false);
      } catch (err) {
        if (!active) return;
        console.error("Error fetching exams:", err);
        setExams([]);
        setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchExams();
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const collator = useMemo(() => new Intl.Collator(language, { numeric: true, sensitivity: "base" }), [language]);
  const query = search.trim().toLowerCase();
  const searching = query !== "";

  // The categories the exams have, by name; when no exam has one, there is no category filter.
  const categoryOptions = useMemo(() => {
    const byValue = new Map();
    exams.forEach((exam) => {
      const examCat = examCategory(exam);
      if (examCat && !byValue.has(examCat.value)) byValue.set(examCat.value, examCat);
    });
    return [...byValue.values()]
      .map((examCat) => ({ value: examCat.value, label: examCat.label || t("exam_bank.untitled") }))
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [exams, collator, t]);
  const categoryLabel = (examCat) => categoryOptions.find((o) => o.value === examCat.value)?.label || examCat.label;

  // The exams that match the search and the category: the status chips count these.
  const matching = useMemo(
    () =>
      exams.filter((exam) => {
        const matchesSearch =
          !query || [exam.name, exam.description].some((text) => (text || "").toLowerCase().includes(query));
        const matchesCategory = category === "all" || examCategory(exam)?.value === category;
        return matchesSearch && matchesCategory;
      }),
    [exams, query, category]
  );
  const statusCounts = useMemo(() => {
    const counts = {};
    matching.forEach((exam) => {
      const value = examStatus(exam);
      counts[value] = (counts[value] || 0) + 1;
    });
    return counts;
  }, [matching]);
  const results = useMemo(
    () =>
      (status === "all" ? matching : matching.filter((exam) => examStatus(exam) === status))
        .slice()
        .sort(sorter(ordering, collator)),
    [matching, status, ordering, collator]
  );

  // A page left empty (fewer results) shows the last page there is.
  const pageCount = searching ? 1 : Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageExams = searching ? results : results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // A new search, filter or order starts from the first page.
  const restart = (setter) => (value) => {
    setter(value);
    setPage(1);
  };
  const changeSearch = restart(setSearch);
  const changeStatus = restart(setStatus);
  const changeCategory = restart(setCategory);
  const changeOrdering = restart(setOrdering);
  // Name: A–Z, Z–A, back to the newest first; created: oldest first, newest first.
  const sortBy = (field) =>
    changeOrdering(
      ordering === field ? `-${field}` : ordering === `-${field}` && field !== "created" ? DEFAULT_ORDERING : field
    );

  const filtersActive = searching || status !== "all" || category !== "all";
  // Every filter back to "all"; the sort order stays.
  const clearAll = () => {
    setSearch("");
    setStatus("all");
    setCategory("all");
    setPage(1);
  };

  // The pager under the list brings the new page's top into view.
  const goToPage = (next, { scroll = false } = {}) => {
    setPage(next);
    if (!scroll) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    resultsRef.current?.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
  };

  const sortOptions = [
    { value: "-created", label: t("question_list.sort_options.newest") },
    { value: "created", label: t("question_list.sort_options.oldest") },
    { value: "name", label: t("question_list.sort_options.name_asc") },
    { value: "-name", label: t("question_list.sort_options.name_desc") },
  ];

  const statusItems = Object.entries(EXAM_STATUS_TONES).map(([value, tone]) => ({
    value,
    label: t(`exam_bank.status.${value}`),
    count: statusCounts[value] || 0,
    tone,
  }));

  const selectCategoryOptions = [{ value: "all", label: t("exam_bank.filters.all_categories") }, ...categoryOptions];
  // react-select itself: the shared Select has no way to name the control for screen readers.
  const filters =
    categoryOptions.length > 0
      ? [
          {
            key: "category",
            active: category !== "all",
            node: (
              <ReactSelect
                aria-label={t("exam_bank.columns.category")}
                styles={filterSelectStyles}
                theme={selectTheme}
                options={selectCategoryOptions}
                value={selectCategoryOptions.find((o) => o.value === category) || null}
                onChange={(option) => changeCategory(option?.value || "all")}
                placeholder={t("exam_bank.filters.all_categories")}
                noOptionsMessage={() => t("table.no_options")}
                isSearchable={categoryOptions.length > 8}
              />
            ),
          },
        ]
      : [];

  const examName = (exam) => exam.name?.trim() || t("exam_bank.untitled");
  const categoryLine = (exam, className) => {
    const examCat = examCategory(exam);
    if (!examCat) return null;
    return <p className={`text-xs text-slate-500 ${className}`}>{t("exam_bank.view.category", { category: categoryLabel(examCat) })}</p>;
  };

  let list;
  if (results.length === 0) {
    list = (
      <EmptyState
        Icon={MagnifyingGlassIcon}
        title={t("exam_bank.list.no_results")}
        hint={t("exam_bank.select_tests.no_results_hint")}
        onClear={filtersActive ? clearAll : undefined}
      />
    );
  } else if (wide) {
    list = (
      // relative: the caption is positioned absolutely; it stays inside the scroll box.
      <div className="relative min-w-0 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <caption className="sr-only">{t("nav.exam_bank")}</caption>
          <thead className="bg-slate-50">
            <tr>
              <SortHeader field="name" ordering={ordering} onSort={sortBy} className="w-full">
                {t("exam_taking.exam")}
              </SortHeader>
              <th scope="col" className={headerCellClass}>
                {t("exam_bank.columns.status")}
              </th>
              <th scope="col" className={`${headerCellClass} text-right`}>
                {t("exam_bank.columns.tests")}
              </th>
              <SortHeader field="created" ordering={ordering} onSort={sortBy} className="whitespace-nowrap">
                {t("exam_bank.columns.created")}
              </SortHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageExams.map((exam) => {
              const href = examHref(exam);
              const description = examDescription(exam);
              const tests = exam.tests_count ?? 0;
              // A name alone sits level with the icon; longer text starts at its top.
              const oneLine = !description && !examCategory(exam);
              return (
                <tr key={exam.uuid} {...rowLink(href)} className="cursor-pointer transition-colors duration-150 hover:bg-slate-50/80">
                  {/* The row's header: moving through a column, a screen reader names the exam. */}
                  <th scope="row" className="px-4 py-3 text-left text-sm font-normal">
                    <div className={`flex min-w-[18rem] gap-3 ${oneLine ? "items-center" : "items-start"}`}>
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600"
                        aria-hidden="true"
                      >
                        <ClipboardDocumentListIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 max-w-3xl break-words pt-px">
                        <Link
                          to={href}
                          className={`rounded font-medium text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
                        >
                          {examName(exam)}
                        </Link>
                        {description && <p className="mt-0.5 line-clamp-2 text-slate-500">{description}</p>}
                        {categoryLine(exam, "mt-1")}
                      </div>
                    </div>
                  </th>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    <StatusButton exam={exam} onChange={openStatus} />
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums ${
                      tests > 0 ? "font-medium text-slate-700" : "text-slate-500"
                    }`}
                  >
                    {tests}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-500">
                    {formatDate(exam.created, language)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  } else {
    list = (
      <ul className="flex flex-col gap-2.5">
        {pageExams.map((exam) => {
          const href = examHref(exam);
          const description = examDescription(exam);
          return (
            <li
              key={exam.uuid}
              {...rowLink(href)}
              className={`cursor-pointer break-words p-3.5 transition duration-200 hover:border-slate-300 ${panelClass}`}
            >
              <Link
                to={href}
                className={`rounded text-[15px] font-semibold leading-snug text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
              >
                {examName(exam)}
              </Link>
              {description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{description}</p>}
              {categoryLine(exam, "mt-1.5")}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <StatusButton exam={exam} onChange={openStatus} />
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {[t("exam_taking.tests_count", { count: exam.tests_count ?? 0 }), formatDate(exam.created, language)]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </li>
          );
        })}
      </ul>
    );
  }

  let body;
  if (loading) {
    body = (
      <div className={`flex items-center justify-center py-16 ${panelClass}`}>
        <div className="text-center" role="status">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true" />
          <p className="text-sm text-slate-500">{t("exam_bank.list.loading")}</p>
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div role="alert" className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {t("exam_bank.list.load_error")}
      </div>
    );
  } else if (exams.length === 0) {
    body = <EmptyState Icon={ClipboardDocumentListIcon} title={t("exam_bank.list.empty_title")} hint={t("exam_bank.list.empty_hint")} />;
  } else {
    body = (
      <>
        <FilterChips
          label={t("question_list.status_filter")}
          allLabel={t("question_list.status_all")}
          allCount={matching.length}
          items={statusItems}
          selected={status}
          onSelect={changeStatus}
        />

        <FilterPanel
          search={search}
          onSearchChange={changeSearch}
          searchPlaceholder={t("exam_bank.list.search_by")}
          searchLabel={t("exam_bank.list.search_label")}
          filters={filters}
          canClear={filtersActive}
          onClearAll={clearAll}
        />

        <div ref={resultsRef} className="scroll-mt-4 lg:scroll-mt-6">
          <ResultsBar
            countText={t("exam_bank.list.count", { count: results.length })}
            page={currentPage}
            pageCount={pageCount}
            ordering={ordering}
            onOrderingChange={changeOrdering}
            sortOptions={sortOptions}
            onPage={goToPage}
          />
        </div>

        {list}

        {pageExams.length > 0 && (
          <div className="flex justify-center">
            <Pager
              page={currentPage}
              pageCount={pageCount}
              onPage={(next) => goToPage(next, { scroll: true })}
              label={t("question_list.pagination_bottom")}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* The module's guide: its "?" right after the title (the exam's page has the same one). */}
          <HelpTitle helpId="exams" helpTitle={t("help.exams.title")} guide={<ExamsGuide />}>
            {t("nav.exam_bank")}
          </HelpTitle>
          <p className="mt-1 text-sm text-slate-500">{t("exam_bank.list.intro")}</p>
        </div>
        <Link to="/exams_create/" className={createButtonClass}>
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
          {t("exam_bank.list.create_exam")}
        </Link>
      </div>

      {body}

      <ExamStatusDialog
        isOpen={statusOpen}
        exam={statusExam}
        onClose={() => setStatusOpen(false)}
        onChanged={(changed) =>
          setExams((prev) => prev.map((exam) => (exam.uuid === changed.uuid ? { ...exam, status: changed.status, updated: changed.updated } : exam)))
        }
      />
    </div>
  );
}
